// Costs — redesigned 2026-04-21.
//
// Mirrors BudgetsPage layout: state chips + searchable sidebar + main
// (summary or project detail) + right rail with budget-vs-current donut
// and variance.
//
// Data flows:
//   /bead/projects            → project list (state, totals)
//   /bead/costs/rollup-by-project → per-project budget / at-current /
//                                    variance / priced-line-count
//   /bead/costs               → SKU-level rows with editable current
//                                price (one price propagates across
//                                every project that uses that SKU)
//   /bead/projects/:id/budget → per-project budget lines (for detail
//                                view we join these to /bead/costs rows
//                                on line_item to render per-line-item
//                                variance with inline price editing)
//   /bead/vendors             → vendor dropdown values
//   PUT /bead/costs/:id       → update a SKU's current price or vendor
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type Project = {
  id: string; project_code: string; project_name: string;
  county: string; tech_type: string;
  awarded_bsl_count: number; location_count: number;
  budget_line_count: number; total_budget_usd: string | null;
  state: string;
};
type CostItem = {
  id: string; item_key: string; line_item: string;
  category: string | null; unit: string | null;
  current_unit_cost_usd: string | null;
  vendor: string | null; quoted_at: string | null;
  project_count: number; total_budgeted_qty: string | null;
  avg_budgeted_unit_cost: string | null;
  min_budgeted_unit_cost: string | null; max_budgeted_unit_cost: string | null;
  total_budgeted_cost: string | null;
  total_at_current_prices: string | null;
  variance_usd: string | null;
};
type Rollup = {
  project_id: string; project_code: string; project_name: string;
  tech_type: string; total_budgeted: string; total_at_current: string;
  variance_usd: string; priced_line_count: number; total_line_count: number;
};
type BudgetLine = {
  id: string; category: string; subcategory: string | null;
  line_item: string; description: string | null; unit: string | null;
  quantity: string | null; unit_cost_usd: string | null;
  total_cost_usd: string | null;
  federal_share_usd: string | null; match_share_usd: string | null;
};
type Vendor = { id: string; name: string };

const fmtUSD = (n: any, keepZero = false) => {
  const v = Number(n);
  if (!isFinite(v)) return '—';
  if (!keepZero && v === 0) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(0)}k`;
  return `${sign}$${Math.round(abs).toLocaleString()}`;
};
const fmtUnit = (n: any) => {
  const v = Number(n);
  if (!isFinite(v) || v === 0) return '—';
  return v < 10 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
};
const fmtNum = (n: any) => {
  const v = Number(n);
  return isFinite(v) ? Math.round(v).toLocaleString() : '—';
};
// 2026-05-31: dynamic state list derived from loaded projects (no more
// WY/CO/NE hardcode). STATE_NAME provides display labels for known states;
// unknown codes fall through to the 2-letter code as the label.
const STATE_NAME: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AR: 'Arkansas', AZ: 'Arizona', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DC: 'D.C.', DE: 'Delaware', FL: 'Florida',
  GA: 'Georgia', HI: 'Hawaii', IA: 'Iowa', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', MA: 'Massachusetts',
  MD: 'Maryland', ME: 'Maine', MI: 'Michigan', MN: 'Minnesota', MO: 'Missouri',
  MS: 'Mississippi', MT: 'Montana', NC: 'North Carolina', ND: 'North Dakota',
  NE: 'Nebraska', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NV: 'Nevada', NY: 'New York', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VA: 'Virginia', VT: 'Vermont',
  WA: 'Washington', WI: 'Wisconsin', WV: 'West Virginia', WY: 'Wyoming',
};
const STATE_ORDER: string[] = []; // populated dynamically below from project list

export default function CostsPage() {
  const [stateFilter, setStateFilter] = useState<'all' | string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: 'total' | 'variance' | 'priced' | 'state' | 'name'; dir: 'asc' | 'desc' }>({ key: 'total', dir: 'desc' });

  const qc = useQueryClient();
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['bead-projects'], queryFn: () => api.get('/bead/projects'),
  });
  const { data: costItems = [] } = useQuery<CostItem[]>({
    queryKey: ['bead-costs'], queryFn: () => api.get('/bead/costs'),
  });
  const { data: rollup = [] } = useQuery<Rollup[]>({
    queryKey: ['bead-costs-rollup'], queryFn: () => api.get('/bead/costs/rollup-by-project'),
  });
  const { data: vendors = [] } = useQuery<Vendor[]>({
    queryKey: ['bead-vendors'], queryFn: () => api.get('/bead/vendors'),
  });
  const { data: projectBudget } = useQuery<{ lines: BudgetLine[]; totals: any }>({
    queryKey: ['bead-budget', selectedId],
    queryFn: () => selectedId ? api.get(`/bead/projects/${selectedId}/budget`) : Promise.reject(),
    enabled: !!selectedId,
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => api.put(`/bead/costs/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bead-costs'] });
      qc.invalidateQueries({ queryKey: ['bead-costs-rollup'] });
      qc.invalidateQueries({ queryKey: ['bead-budget'] });
      qc.invalidateQueries({ queryKey: ['pricing-model'] });
    },
  });

  // --- rollups --------------------------------------------------------
  // Portfolio totals are taken from project-rollup, not SKU totals —
  // SKU totals are per-SKU-across-projects and not comparable to the
  // project-rollup total (which is per-project-sum-of-budget).
  const stateBudgets = useMemo(() => {
    const s: Record<string, { projects: number; budget: number; atCurrent: number; variance: number; pricedLines: number; totalLines: number }> = {};
    const byId = new Map(projects.map((p) => [p.id, p]));
    for (const r of rollup) {
      const p = byId.get(r.project_id); if (!p) continue;
      const st = p.state || '??';
      if (!s[st]) s[st] = { projects: 0, budget: 0, atCurrent: 0, variance: 0, pricedLines: 0, totalLines: 0 };
      s[st].projects += 1;
      s[st].budget += Number(r.total_budgeted || 0);
      s[st].atCurrent += Number(r.total_at_current || 0);
      s[st].variance += Number(r.variance_usd || 0);
      s[st].pricedLines += r.priced_line_count || 0;
      s[st].totalLines += r.total_line_count || 0;
    }
    return s;
  }, [rollup, projects]);
  const grand = useMemo(() => {
    const out = { projects: 0, budget: 0, atCurrent: 0, variance: 0, pricedLines: 0, totalLines: 0 };
    for (const s of Object.values(stateBudgets)) {
      out.projects += s.projects; out.budget += s.budget; out.atCurrent += s.atCurrent;
      out.variance += s.variance; out.pricedLines += s.pricedLines; out.totalLines += s.totalLines;
    }
    return out;
  }, [stateBudgets]);

  // --- sidebar filter/group ------------------------------------------
  const q = query.trim().toLowerCase();
  const visible = useMemo(() => {
    const ok = (p: Project) => stateFilter === 'all' ? true : p.state === stateFilter;
    return projects.filter(ok).filter((p) => {
      if (!q) return true;
      return p.project_code.toLowerCase().includes(q)
        || p.project_name.toLowerCase().includes(q)
        || (p.county || '').toLowerCase().includes(q);
    });
  }, [projects, stateFilter, q]);
  const rollupById = useMemo(() => new Map(rollup.map((r) => [r.project_id, r])), [rollup]);
  const grouped = useMemo(() => {
    const byState: Record<string, Project[]> = {};
    for (const p of visible) { const s = p.state || '??'; (byState[s] ||= []).push(p); }
    for (const s of Object.keys(byState)) {
      byState[s].sort((a, b) => Number(b.total_budget_usd || 0) - Number(a.total_budget_usd || 0));
    }
    return byState;
  }, [visible]);

  const active = selectedId ? projects.find((p) => p.id === selectedId) : undefined;
  if (active && stateFilter !== 'all' && active.state !== stateFilter) setSelectedId(null);
  const activeRollup = selectedId ? rollupById.get(selectedId) : undefined;

  // --- summary rows --------------------------------------------------
  const summaryRows = useMemo(() => {
    const inScope = stateFilter === 'all' ? projects : projects.filter((p) => p.state === stateFilter);
    const rows = inScope.map((p) => ({ project: p, rollup: rollupById.get(p.id) }));
    const sorted = [...rows].sort((a, b) => {
      const d = sort.dir === 'asc' ? 1 : -1;
      switch (sort.key) {
        case 'total':    return d * (Number(a.rollup?.total_budgeted || 0) - Number(b.rollup?.total_budgeted || 0));
        case 'variance': return d * (Number(a.rollup?.variance_usd || 0) - Number(b.rollup?.variance_usd || 0));
        case 'priced':   return d * ((a.rollup?.priced_line_count || 0) - (b.rollup?.priced_line_count || 0));
        case 'state':    return d * ((a.project.state || '').localeCompare(b.project.state || ''));
        case 'name':     return d * ((a.project.project_name || '').localeCompare(b.project.project_name || ''));
      }
    });
    return sorted;
  }, [projects, rollupById, stateFilter, sort]);

  // Any prices entered ANYWHERE in the scope?
  const scopeHasPrice = useMemo(() => {
    if (selectedId && activeRollup) return (activeRollup.priced_line_count || 0) > 0;
    if (stateFilter !== 'all') return (stateBudgets[stateFilter]?.pricedLines || 0) > 0;
    return grand.pricedLines > 0;
  }, [selectedId, activeRollup, stateFilter, stateBudgets, grand]);

  // Scope-dependent headline numbers for right rail + KPIs
  const scope = useMemo(() => {
    if (selectedId && activeRollup) {
      return {
        label: 'Project total', sub: `${activeRollup.priced_line_count}/${activeRollup.total_line_count} lines priced`,
        budget: Number(activeRollup.total_budgeted || 0),
        atCurrent: Number(activeRollup.total_at_current || 0),
        variance: Number(activeRollup.variance_usd || 0),
      };
    }
    if (stateFilter !== 'all') {
      const s = stateBudgets[stateFilter];
      return {
        label: `${STATE_NAME[stateFilter] || stateFilter} total`,
        sub: s ? `${s.pricedLines}/${s.totalLines} lines priced · ${s.projects} projects` : '',
        budget: s?.budget || 0, atCurrent: s?.atCurrent || 0, variance: s?.variance || 0,
      };
    }
    return {
      label: 'Portfolio total',
      sub: `${grand.pricedLines}/${grand.totalLines} lines priced · ${grand.projects} projects`,
      budget: grand.budget, atCurrent: grand.atCurrent, variance: grand.variance,
    };
  }, [selectedId, activeRollup, stateFilter, stateBudgets, grand]);

  return (
    <div style={{ padding: 20, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <h1 style={{ fontSize: 20, margin: 0 }}>Pricing Overrides</h1>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Budget vs current market. Enter a current unit price on a SKU — savings or overruns flow to every project that uses it.
        </div>
      </div>

      {/* Top strip: state chips with variance */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, marginBottom: 16 }}>
        <Chip active={stateFilter === 'all'} onClick={() => { setStateFilter('all'); setSelectedId(null); }}
          primary="All projects" secondary={`${grand.projects} · ${fmtUSD(grand.budget)}`}
          variance={grand.variance} priced={grand.pricedLines} total={grand.totalLines} />
        {Object.keys(stateBudgets).sort().map((s) => (
          <Chip key={s} active={stateFilter === s}
            onClick={() => { setStateFilter(s); setSelectedId(null); }}
            primary={STATE_NAME[s] || s}
            secondary={`${stateBudgets[s].projects} · ${fmtUSD(stateBudgets[s].budget)}`}
            variance={stateBudgets[s].variance}
            priced={stateBudgets[s].pricedLines} total={stateBudgets[s].totalLines} />
        ))}
      </div>

      {!selectedId && (
        <div style={{ padding: 16, background: '#0b1220', color: '#fff', borderRadius: 8, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8' }}>{scope.label}</div>
            <div style={{ fontSize: 30, fontWeight: 700 }}>{fmtUSD(scope.budget, true)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{scope.sub}</div>
          </div>
          {scopeHasPrice && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8' }}>
                {scope.variance === 0 ? 'No variance yet' : scope.variance > 0 ? 'Savings' : 'Overrun'}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: scope.variance > 0 ? '#34d399' : scope.variance < 0 ? '#f87171' : '#fff' }}>
                {scope.variance === 0 ? '—' : `${scope.variance >= 0 ? '+' : ''}${fmtUSD(scope.variance)}`}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>At current prices: {fmtUSD(scope.atCurrent, true)}</div>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: selectedId ? '260px 1fr 280px' : '260px 1fr', gap: 16 }}>
        {/* ---------- Sidebar ---------- */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#fff', maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={stateFilter === 'all' ? 'Search projects…' : `Search ${STATE_NAME[stateFilter] || stateFilter}…`}
              style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, outline: 'none' }} />
          </div>
          {Object.keys(grouped).sort().map((s) => (
            <div key={s}>
              <div style={{ padding: '8px 10px 4px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#0f172a', background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
                {STATE_NAME[s] || s}{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                  · {grouped[s].length} · {fmtUSD(grouped[s].reduce((a, b) => a + Number(b.total_budget_usd || 0), 0))}
                </span>
              </div>
              {grouped[s].map((p) => {
                const isActive = selectedId === p.id;
                const r = rollupById.get(p.id);
                const v = Number(r?.variance_usd || 0);
                return (
                  <button key={p.id} onClick={() => setSelectedId(p.id)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '6px 10px', background: isActive ? '#ede9fe' : 'transparent',
                      border: 'none', borderLeft: isActive ? '3px solid #8b5cf6' : '3px solid transparent',
                      cursor: 'pointer', fontSize: 12,
                    }}
                    title={`${p.project_code} · ${p.county} County · ${p.awarded_bsl_count} BSLs`}>
                    <div style={{ fontWeight: 600, color: '#111827' }}>{p.project_name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                      {p.project_code} · {fmtUSD(r?.total_budgeted ?? p.total_budget_usd)}
                      {Math.abs(v) > 0 && (
                        <span style={{ marginLeft: 6, color: v > 0 ? '#047857' : '#b91c1c', fontWeight: 600 }}>
                          {v > 0 ? '+' : ''}{fmtUSD(v)}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
          {visible.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
              No projects match “{query}”
            </div>
          )}
        </div>

        {/* ---------- Main ---------- */}
        <div>
          {selectedId && active && projectBudget ? (
            <ProjectCostDetail project={active} lines={projectBudget.lines}
              costItems={costItems} vendors={vendors}
              onSave={(id, body) => updateMut.mutate({ id, body })}
              onBack={() => setSelectedId(null)} />
          ) : (
            <SummaryTable rows={summaryRows} sort={sort} setSort={setSort}
              scopeLabel={stateFilter === 'all' ? 'All projects' : (STATE_NAME[stateFilter] || stateFilter)}
              onRowClick={(id) => setSelectedId(id)} showVariance={scopeHasPrice} />
          )}
        </div>

        {/* ---------- Right rail (project detail only) ---------- */}
        {selectedId && (
        <div>
          <div style={{ padding: 16, background: '#0b1220', color: '#fff', borderRadius: 8, position: 'sticky', top: 20 }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8', marginBottom: 4 }}>
              {scope.label}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{fmtUSD(scope.budget, true)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{scope.sub}</div>

            <VarianceBar budget={scope.budget} atCurrent={scope.atCurrent} variance={scope.variance} />
          </div>
        </div>
        )}
      </div>

      {projects.length === 0 && (
        <div style={{ color: 'var(--text-muted)', marginTop: 20 }}>
          No budget data ingested yet.
        </div>
      )}
    </div>
  );
}

// ============================================================
// Chip with secondary + small variance pill
// ============================================================
function Chip({ primary, secondary, active, onClick, variance = 0, priced, total }:
  { primary: string; secondary?: string; active?: boolean; onClick: () => void; variance?: number; priced?: number; total?: number }) {
  const v = variance;
  const pill = Math.abs(v) > 0;
  return (
    <button onClick={onClick} style={{
      padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
      border: active ? '2px solid #8b5cf6' : '1px solid var(--border)',
      background: active ? '#ede9fe' : '#fff',
      color: active ? '#5b21b6' : '#111827',
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 130,
    }}>
      <span style={{ fontSize: 12, fontWeight: 700 }}>{primary}</span>
      {secondary && <span style={{ fontSize: 10, color: active ? '#7c3aed' : 'var(--text-muted)' }}>{secondary}</span>}
      {pill && (
        <span style={{ fontSize: 10, marginTop: 3, padding: '1px 4px', borderRadius: 3,
          background: v > 0 ? '#dcfce7' : '#fee2e2',
          color: v > 0 ? '#047857' : '#b91c1c', fontWeight: 600,
        }}>
          {v >= 0 ? '+' : ''}{fmtUSD(v)} variance
        </span>
      )}
    </button>
  );
}

// ============================================================
// VarianceBar: simple horizontal bar showing budget vs current,
// plus variance amount underneath
// ============================================================
function VarianceBar({ budget, atCurrent, variance }: { budget: number; atCurrent: number; variance: number }) {
  if (!budget) return null;
  const ratio = Math.min(Math.max(atCurrent / budget, 0), 2);
  const good = variance >= 0;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
        <span>Budget</span><span>{fmtUSD(budget, true)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
        <span>At current prices</span><span>{fmtUSD(atCurrent, true)}</span>
      </div>
      <div style={{ height: 8, background: '#1e293b', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
        <div style={{ height: '100%', width: `${Math.min(ratio * 50, 100)}%`, background: good ? '#10b981' : '#ef4444' }} />
        <div style={{ position: 'absolute', top: 0, left: '50%', height: '100%', width: 1, background: '#64748b' }} />
      </div>
      <div style={{ marginTop: 10, padding: '6px 8px', borderRadius: 4, fontSize: 13, fontWeight: 700, textAlign: 'center',
        background: variance === 0 ? '#1e293b' : good ? '#064e3b' : '#7f1d1d',
        color: variance === 0 ? '#94a3b8' : '#fff',
      }}>
        {variance === 0 ? 'No prices entered' : `${good ? 'Savings' : 'Overrun'} ${fmtUSD(variance)}`}
      </div>
    </div>
  );
}

// ============================================================
// Summary table (all / state scope)
// ============================================================
function SummaryTable({ rows, sort, setSort, onRowClick, scopeLabel, showVariance }: {
  rows: Array<{ project: Project; rollup: Rollup | undefined }>;
  sort: { key: 'total' | 'variance' | 'priced' | 'state' | 'name'; dir: 'asc' | 'desc' };
  setSort: (s: any) => void;
  onRowClick: (id: string) => void;
  scopeLabel: string;
  showVariance: boolean;
}) {
  const toggle = (k: any) => {
    if (sort.key === k) setSort({ key: k, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    else setSort({ key: k, dir: k === 'total' || k === 'variance' || k === 'priced' ? 'desc' : 'asc' });
  };
  const arrow = (k: string) => sort.key === k ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>{scopeLabel} · {rows.length} project{rows.length === 1 ? '' : 's'}</div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <Th onClick={() => toggle('state')}>State{arrow('state')}</Th>
              <Th onClick={() => toggle('name')}>Project{arrow('name')}</Th>
              <Th>County</Th>
              <Th right onClick={() => toggle('total')}>Budget{arrow('total')}</Th>
              {showVariance && <Th right>At current</Th>}
              {showVariance && <Th right onClick={() => toggle('variance')}>Variance{arrow('variance')}</Th>}
              <Th right onClick={() => toggle('priced')}>Priced{arrow('priced')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ project: p, rollup: r }) => {
              const v = Number(r?.variance_usd || 0);
              return (
                <tr key={p.id} onClick={() => onRowClick(p.id)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '')}>
                  <Td><span style={{ padding: '1px 5px', background: '#e0e7ff', color: '#3730a3', fontSize: 10, fontWeight: 700, borderRadius: 3 }}>{p.state}</span></Td>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{p.project_name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.project_code}</div>
                  </Td>
                  <Td>{p.county}</Td>
                  <Td right style={{ fontWeight: 600 }}>{fmtUSD(r?.total_budgeted ?? p.total_budget_usd)}</Td>
                  {showVariance && <Td right>{fmtUSD(r?.total_at_current)}</Td>}
                  {showVariance && <Td right>
                    {Math.abs(v) > 0 ? (
                      <span style={{ padding: '1px 5px', borderRadius: 3, fontWeight: 700,
                        background: v > 0 ? '#dcfce7' : '#fee2e2',
                        color: v > 0 ? '#047857' : '#b91c1c' }}>
                        {v >= 0 ? '+' : ''}{fmtUSD(v)}
                      </span>
                    ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </Td>}
                  <Td right>{r ? `${r.priced_line_count}/${r.total_line_count}` : '—'}</Td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={showVariance ? 7 : 5} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>No projects.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function Th({ children, right, onClick }: any) {
  return <th onClick={onClick} style={{ textAlign: right ? 'right' : 'left', padding: '8px 10px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: '#111827', borderBottom: '1px solid var(--border)', cursor: onClick ? 'pointer' : 'default', userSelect: 'none' }}>{children}</th>;
}
function Td({ children, right, style }: any) {
  return <td style={{ padding: '8px 10px', textAlign: right ? 'right' : 'left', fontVariantNumeric: right ? 'tabular-nums' : undefined, ...style }}>{children}</td>;
}

// ============================================================
// Project cost detail — per-line variance with inline price editing
// ============================================================
function ProjectCostDetail({ project: p, lines, costItems, vendors, onSave, onBack }: {
  project: Project;
  lines: BudgetLine[];
  costItems: CostItem[];
  vendors: Vendor[];
  onSave: (id: string, body: any) => void;
  onBack: () => void;
}) {
  // Map line_item key → cost row so we can merge per-project budget
  // rows with their portfolio SKU (priced or not).
  const costByKey = useMemo(() => {
    const m = new Map<string, CostItem>();
    for (const c of costItems) m.set(c.item_key, c);
    return m;
  }, [costItems]);

  type Row = {
    budget: BudgetLine;
    cost?: CostItem;
    budgetTotal: number;
    atCurrent: number;
    variance: number;
    hasPrice: boolean;
  };
  const rows: Row[] = useMemo(() => lines.map((bl) => {
    const key = (bl.line_item || '').trim().toLowerCase();
    const c = costByKey.get(key);
    const qty = Number(bl.quantity || 0);
    const cur = c?.current_unit_cost_usd != null ? Number(c.current_unit_cost_usd) : null;
    const budgetTotal = Number(bl.total_cost_usd || 0);
    const atCurrent = cur != null ? qty * cur : budgetTotal;
    const variance = cur != null ? budgetTotal - (qty * cur) : 0;
    return { budget: bl, cost: c, budgetTotal, atCurrent, variance, hasPrice: cur != null };
  }), [lines, costByKey]);

  const totalBudget = rows.reduce((a, r) => a + r.budgetTotal, 0);
  const totalCurrent = rows.reduce((a, r) => a + r.atCurrent, 0);
  const totalVariance = rows.reduce((a, r) => a + r.variance, 0);

  return (
    <div>
      <div style={{ marginBottom: 14, padding: 14, background: '#0b1220', color: '#fff', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 17, margin: 0 }}>{p.project_name}</h2>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
            {p.project_code} · {p.county} County, {p.state} · {p.tech_type} · {p.awarded_bsl_count} BSLs · {rows.length} line items
          </div>
        </div>
        <button onClick={onBack} style={{ padding: '6px 12px', background: 'transparent', color: '#fff', border: '1px solid #334', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>← Back to list</button>
      </div>

      {/* Mini summary inside detail */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
        <KpiCell label="Budget" value={fmtUSD(totalBudget, true)} />
        <KpiCell label="At current" value={fmtUSD(totalCurrent, true)} />
        <KpiCell label={totalVariance >= 0 ? 'Savings' : 'Overrun'}
          value={`${totalVariance >= 0 ? '+' : ''}${fmtUSD(totalVariance) === '—' ? '$0' : fmtUSD(totalVariance)}`}
          tone={totalVariance === 0 ? 'neutral' : totalVariance > 0 ? 'good' : 'bad'} />
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <Th>Line item</Th>
              <Th right>Qty</Th>
              <Th right>Budgeted unit $</Th>
              <Th>Current unit $</Th>
              <Th>Vendor</Th>
              <Th right>Budget total</Th>
              <Th right>At current</Th>
              <Th right>Variance</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.budget.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <Td>
                  <div style={{ fontWeight: 500 }}>{r.budget.line_item}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{r.budget.category}</div>
                </Td>
                <Td right>{r.budget.quantity != null ? Number(r.budget.quantity).toLocaleString() : '—'}</Td>
                <Td right>{fmtUnit(r.budget.unit_cost_usd)}</Td>
                <Td>
                  {r.cost ? (
                    <input type="number" step="0.0001" min="0"
                      defaultValue={r.cost.current_unit_cost_usd ?? ''}
                      placeholder={fmtUnit(r.budget.unit_cost_usd)}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v === '' && r.cost!.current_unit_cost_usd == null) return;
                        const num = v === '' ? null : Number(v);
                        if (num !== null && !isFinite(num)) return;
                        if (num === 0) { e.target.value = r.cost!.current_unit_cost_usd ?? ''; return; }
                        onSave(r.cost!.id, { current_unit_cost_usd: num });
                      }}
                      style={{ width: 90, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
                    />
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>no SKU</span>
                  )}
                </Td>
                <Td>
                  {r.cost ? (
                    <select value={r.cost.vendor ?? ''}
                      onChange={(e) => onSave(r.cost!.id, { vendor: e.target.value || null })}
                      style={{ width: 100, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, background: '#fff' }}>
                      <option value="">—</option>
                      {vendors.map((vd) => <option key={vd.id} value={vd.name}>{vd.name}</option>)}
                    </select>
                  ) : null}
                </Td>
                <Td right style={{ fontWeight: 600 }}>{fmtUSD(r.budgetTotal, true)}</Td>
                <Td right>{r.hasPrice ? fmtUSD(r.atCurrent, true) : <span style={{ color: 'var(--text-muted)' }}>—</span>}</Td>
                <Td right>
                  {r.hasPrice && Math.abs(r.variance) > 0 ? (
                    <span style={{ padding: '1px 5px', borderRadius: 3, fontWeight: 700,
                      background: r.variance > 0 ? '#dcfce7' : '#fee2e2',
                      color: r.variance > 0 ? '#047857' : '#b91c1c' }}>
                      {r.variance >= 0 ? '+' : ''}{fmtUSD(r.variance)}
                    </span>
                  ) : <span style={{ color: 'var(--text-muted)' }}>{r.hasPrice ? '$0' : 'enter $'}</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)' }}>
        Note: current prices are shared across all projects that use the same line item.
        Setting a price here updates the variance for every project using this SKU.
      </div>
    </div>
  );
}

function KpiCell({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' | 'neutral' }) {
  const bg = tone === 'good' ? '#064e3b' : tone === 'bad' ? '#7f1d1d' : '#0b1220';
  return (
    <div style={{ background: bg, color: '#fff', padding: '10px 12px', borderRadius: 6 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
